import TextEdit from '../../../molecules/TextEdit';
import SelectTextField from '../../../molecules/SelectTextField';
import DateField from '../../../molecules/DateField';
import CurrencyInput from '../../../molecules/CurrencyInput';
import BeneficiarySelectField from './BeneficiarySelectField';
import { BENEFICIARY_TYPE_OPTIONS } from '../../../../types/LegalCase';
import type { ErrorField } from '../../../../types/ErrorField';
import type { Beneficiary, SelectOption } from '../../../../types/LegalCase';
import { getErrorMessage } from '../../../../utils/formErrorUtils';

// Só escritórios no select de responsável (o campo é "quem cuida do caso"). Filtro de UX.
const LAW_FIRM_TYPE_OPTIONS: SelectOption[] = BENEFICIARY_TYPE_OPTIONS.filter(
    (o) => o.value === 'LAW_FIRM',
);

const UF_OPTIONS: SelectOption[] = ['AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT','PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO']
    .map(uf => ({ value: uf, label: uf }));

export interface LegalCaseFormProps {
    isCreating: boolean;
    errorFields: ErrorField[];
    caseType: string; setCaseType: (v: string) => void;
    pole: string; setPole: (v: string) => void;
    status: string; setStatus: (v: string) => void;
    creditorEntity: string; setCreditorEntity: (v: string) => void;
    openedAt: string; setOpenedAt: (v: string) => void;
    externalCaseNumber: string; setExternalCaseNumber: (v: string) => void;
    court: string; setCourt: (v: string) => void;
    district: string; setDistrict: (v: string) => void;
    courtDivision: string; setCourtDivision: (v: string) => void;
    state: string; setState: (v: string) => void;
    courtLink: string; setCourtLink: (v: string) => void;
    claimValue: number; setClaimValue: (v: number) => void;
    responsibleFirmId: string; setResponsibleFirmId: (v: string) => void;
    description: string; setDescription: (v: string) => void;
    caseTypeOptions: SelectOption[];
    poleOptions: SelectOption[];
    statusOptions: SelectOption[];
    // Escritórios cadastrados (já buscados pelo hook de create/edit, filtrados por LAW_FIRM).
    // Evita o form buscar a lista de novo (o Copilot pegou essa chamada redundante).
    lawFirms: Beneficiary[];
    handleCreate: () => void;
}

const LegalCaseForm = (props: LegalCaseFormProps) => {
    const {
        errorFields,
        caseType, setCaseType,
        pole, setPole,
        status, setStatus,
        creditorEntity, setCreditorEntity,
        openedAt, setOpenedAt,
        externalCaseNumber, setExternalCaseNumber,
        court, setCourt,
        district, setDistrict,
        courtDivision, setCourtDivision,
        state, setState,
        courtLink, setCourtLink,
        claimValue, setClaimValue,
        responsibleFirmId, setResponsibleFirmId,
        description, setDescription,
        caseTypeOptions, poleOptions, statusOptions,
        lawFirms,
    } = props;

    return (
        <div className="px-6 py-5 space-y-6 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                <SelectTextField
                    label="Tipo do caso"
                    items={caseTypeOptions}
                    value={caseType}
                    onChange={(e) => setCaseType(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'caseType')}
                />
                <SelectTextField
                    label="Polo"
                    items={poleOptions}
                    value={pole}
                    onChange={(e) => setPole(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'pole')}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                <TextEdit
                    label="Entidade credora"
                    value={creditorEntity}
                    onChange={(e: any) => setCreditorEntity(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'creditorEntity')}
                />
                <SelectTextField
                    label="Situação"
                    items={statusOptions}
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'status')}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                <TextEdit
                    label="Número do processo (CNJ)"
                    value={externalCaseNumber}
                    onChange={(e: any) => setExternalCaseNumber(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'externalCaseNumber')}
                />
                <DateField
                    label="Data de distribuição"
                    value={openedAt}
                    onChange={setOpenedAt}
                    errorMessage={getErrorMessage(errorFields, 'openedAt')}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                <CurrencyInput
                    label="Valor da Causa"
                    prefix="R$"
                    fractionDigits={2}
                    value={claimValue}
                    onChange={setClaimValue}
                    errorMessage={getErrorMessage(errorFields, 'claimValue')}
                />
                <BeneficiarySelectField
                    label="Escritório responsável (opcional)"
                    value={responsibleFirmId}
                    onChange={setResponsibleFirmId}
                    beneficiaries={lawFirms}
                    beneficiaryTypeOptions={LAW_FIRM_TYPE_OPTIONS}
                    isSaving={false}
                    errorFields={errorFields}
                    errorFieldName="responsibleFirmId"
                    allowCreate={false}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                <TextEdit
                    label="Foro / Tribunal"
                    value={court}
                    onChange={(e: any) => setCourt(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'court')}
                />
                <TextEdit
                    label="Comarca"
                    value={district}
                    onChange={(e: any) => setDistrict(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'district')}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                <TextEdit
                    label="Vara"
                    value={courtDivision}
                    onChange={(e: any) => setCourtDivision(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'courtDivision')}
                />
                <SelectTextField
                    label="UF"
                    items={UF_OPTIONS}
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'state')}
                />
            </div>

            <TextEdit
                label="Link do tribunal"
                value={courtLink}
                onChange={(e: any) => setCourtLink(e.target.value)}
                errorMessage={getErrorMessage(errorFields, 'courtLink')}
            />

            <TextEdit
                label="Observações"
                value={description}
                onChange={(e: any) => setDescription(e.target.value)}
                errorMessage={getErrorMessage(errorFields, 'description')}
            />
        </div>
    );
};

export default LegalCaseForm;
